import React, { useMemo, useState } from 'react';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { MediaType } from '../../../codegen/graphql';
import { Modal } from '../common/Modal';
import Button from '../common/Button';
import { ButtonGroup } from '../common/ButtonGroup';
import { Lightbox } from '../ImageTools/Lightbox';
import { useModalRouter } from '../../ModalRouter_context';
import { GET_MEDIA_TRANSLATIONS } from './MediaTranslationsManagerModal';

const TRANSLATION_LANGUAGES = ['FR', 'IT', 'ES', 'NL', 'DE', 'PT'] as const;
type TranslationLang = typeof TRANSLATION_LANGUAGES[number];

const QUALITY_OPTIONS = [
  { value: 'cheap', label: 'Cheap' },
  { value: 'standard', label: 'Standard' },
  { value: 'high', label: 'High' },
] as const;

type NanoBananaQuality = typeof QUALITY_OPTIONS[number]['value'];

const GET_MEDIAS_FOR_TRANSLATION = gql`
  query NanoBananaMediasTranslation_GetMedias($ids: [ID!]!) {
    mediasByIds(ids: $ids) {
      id
      url
      type
      translations {
        lang
        langMediaActive
      }
    }
  }
`;

const TRANSLATE_IMAGES = gql`
  mutation NanoBananaMediasTranslation_Translate(
    $mediaIds: [ID!]!
    $targetLanguages: [String!]!
    $quality: NanoBananaImageQuality!
    $overwriteExisting: Boolean
  ) {
    translateImageUsingNanoBanana(
      mediaIds: $mediaIds
      targetLanguages: $targetLanguages
      quality: $quality
      overwriteExisting: $overwriteExisting
    ) {
      id
      status
    }
  }
`;

type TranslationMedia = {
  id: string;
  url: string;
  type: MediaType;
  translations: Array<{
    lang: string;
    langMediaActive: boolean;
  }>;
};

type NanoBananaMediasQuery = {
  mediasByIds: TranslationMedia[];
};

interface NanoBananaMediasTranslationProps {
  mediaIds: string[];
}

function normalizeMediaIds(mediaIds: string[] | string | undefined): string[] {
  if (Array.isArray(mediaIds)) {
    return mediaIds.filter(Boolean);
  }
  return mediaIds ? [mediaIds] : [];
}

function hasActiveTranslation(media: TranslationMedia, lang: string) {
  return media.translations.some((translation) => (
    translation.langMediaActive && translation.lang === lang
  ));
}

export function NanoBananaMediasTranslation({
  mediaIds,
}: NanoBananaMediasTranslationProps) {
  const modalRouter = useModalRouter();
  return (
    <Modal
      title="Translate images"
      onClose={modalRouter.pop}
      width="medium"
    >
      <NanoBananaMediasTranslationContent mediaIds={normalizeMediaIds(mediaIds)} />
    </Modal>
  );
}

function NanoBananaMediasTranslationContent({
  mediaIds,
}: {
  mediaIds: string[];
}) {
  const modalRouter = useModalRouter();
  const { data } = useSuspenseQuery<NanoBananaMediasQuery>(GET_MEDIAS_FOR_TRANSLATION, {
    variables: { ids: mediaIds },
  });
  const [translateImages, { loading }] = useMutation(TRANSLATE_IMAGES);
  const [quality, setQuality] = useState<NanoBananaQuality>('cheap');
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const medias = data.mediasByIds ?? [];

  const missingLangs = useMemo(() => {
    return TRANSLATION_LANGUAGES.filter((lang) => (
      medias.some((media) => !hasActiveTranslation(media, lang))
    ));
  }, [medias]);

  const [selectedLangs, setSelectedLangs] = useState<TranslationLang[]>(missingLangs);

  const langCounts = useMemo(() => {
    const counts = new Map<TranslationLang, { missing: number; existing: number }>();
    for (const lang of TRANSLATION_LANGUAGES) {
      const existing = medias.filter((media) => hasActiveTranslation(media, lang)).length;
      counts.set(lang, {
        existing,
        missing: medias.length - existing,
      });
    }
    return counts;
  }, [medias]);

  const imagesToGenerate = useMemo(() => {
    return selectedLangs.reduce((sum, lang) => {
      const counts = langCounts.get(lang);
      if (!counts) return sum;
      return sum + (overwriteExisting ? medias.length : counts.missing);
    }, 0);
  }, [selectedLangs, langCounts, overwriteExisting, medias.length]);

  if (!medias.length) {
    return (
      <div className="text-gray-500">
        Media not found.
      </div>
    );
  }

  const toggleLang = (lang: TranslationLang) => {
    setSelectedLangs((current) => (
      current.includes(lang)
        ? current.filter((selected) => selected !== lang)
        : [...current, lang]
    ));
  };

  const handleTranslate = async () => {
    if (!imagesToGenerate) return;
    try {
      await translateImages({
        variables: {
          mediaIds: medias.map((media) => media.id),
          targetLanguages: selectedLangs,
          quality,
          overwriteExisting,
        },
        refetchQueries: medias.map((media) => ({
          query: GET_MEDIA_TRANSLATIONS,
          variables: { mediaId: media.id },
        })),
      });
      modalRouter.pop();
    } catch (error) {
      console.error('Error starting image translation:', error);
      alert('Failed to start image translation. Please try again.');
    }
  };

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">
          {medias.length} {medias.length === 1 ? 'image' : 'images'} selected
        </p>
        <div className="flex items-center gap-2">
          {medias.map((media) => (
            <img
              key={media.id}
              src={media.url}
              alt=""
              className="cursor-pointer h-[50px] w-[50px] rounded-md border border-gray-200 object-cover bg-gray-50"
              onClick={() => setLightboxOpen(true)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700">Target languages</p>
        <div className="grid grid-cols-3 gap-2">
          {TRANSLATION_LANGUAGES.map((lang) => {
            const counts = langCounts.get(lang);
            return (
              <label
                key={lang}
                className="flex items-start gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm"
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={selectedLangs.includes(lang)}
                  onChange={() => toggleLang(lang)}
                />
                <span>
                  <span className="font-medium">{lang}</span>
                  <span className="block text-xs text-gray-500">
                    {counts?.missing ?? 0} missing, {counts?.existing ?? 0} existing
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={overwriteExisting}
          onChange={(event) => setOverwriteExisting(event.target.checked)}
        />
        Overwrite existing translations
      </label>

      <ButtonGroup<NanoBananaQuality>
        label="Quality"
        options={[...QUALITY_OPTIONS]}
        value={quality}
        onChange={setQuality}
      />

      <p className="text-sm text-gray-700">
        {imagesToGenerate} {imagesToGenerate === 1 ? 'image' : 'images'} will be generated
      </p>

      <div className="flex justify-end">
        <Button
          preset="primary"
          onPress={handleTranslate}
          disabled={loading || imagesToGenerate === 0}
        >
          {loading ? 'Starting…' : 'Translate'}
        </Button>
      </div>

      {lightboxOpen && (
        <Lightbox
          className="!z-[1100]"
          medias={medias.map((media) => ({
            id: media.id,
            url: media.url,
            type: media.type,
          }))}
          initialIndex={0}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
}
